/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

import { LogLevel } from "@azure/msal-browser";

const {
  REACT_APP_AZURE_TENANT_NAME: tenantName,
  REACT_APP_AZURE_SIGNUPSIGNIN_FLOW: signUpSignInFlow,
  REACT_APP_AZURE_RESETPASSWORD_FLOW: resetPasswordFlow,
  REACT_APP_AZURE_EDITPASSWORD_FLOW: editPasswordFlow,
  REACT_APP_AZURE_AZURE_CLIENTID: clientId,
  REACT_APP_AZURE_PAMI_API_NAME: pamiApiName,
  REACT_APP_AZURE_PAMI_API_SCOPE_READ: pamiApiScopeRead,
  REACT_APP_AZURE_PAMI_API_SCOPE_WRITE: pamiApiScopeWrite,
} = process.env;


/**
 * Enter here the user flows and custom policies for your B2C application
 * To learn more about user flows, visit: https://docs.microsoft.com/en-us/azure/active-directory-b2c/user-flow-overview
 * To learn more about custom policies, visit: https://docs.microsoft.com/en-us/azure/active-directory-b2c/custom-policy-overview
 */
export const b2cPolicies = {
  names: {
    signUpSignIn: signUpSignInFlow,
    forgotPassword: resetPasswordFlow,
    editProfile: editPasswordFlow,
  },
  authorities: {
    signUpSignIn: {
      authority: `https://${tenantName}.b2clogin.com/${tenantName}.onmicrosoft.com/${signUpSignInFlow}`,
    },
    forgotPassword: {
      authority: `https://${tenantName}.b2clogin.com/${tenantName}.onmicrosoft.com/${resetPasswordFlow}`,
    },
    editProfile: {
      authority: `https://${tenantName}.b2clogin.com/${tenantName}.onmicrosoft.com/${editPasswordFlow}`,
    }
  },
  authorityDomain: `${tenantName}.b2clogin.com`
};

/**
 * Configuration object to be passed to MSAL instance on creation.
 * For a full list of MSAL.js configuration parameters, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md
 */
export const msalConfig = {
  auth: {
    clientId: clientId || "", // This is the ONLY mandatory field that you need to supply.
    authority: b2cPolicies.authorities.signUpSignIn.authority, // Use a sign-up/sign-in user-flow as a default authority
    knownAuthorities: [b2cPolicies.authorityDomain], // Mark your B2C tenant's domain as trusted.
    redirectUri: "/blank.html", // Points to window.location.origin. You must register this URI on Azure Portal/App Registration.
    postLogoutRedirectUri: "/", // Indicates the page to navigate after logout.
    navigateToLoginRequestUrl: true, // If "true", will navigate back to the original request location before processing the auth code response.
  },
  cache: {
    cacheLocation: "sessionStorage", // Configures cache location. "sessionStorage" is more secure, but "localStorage" gives you SSO between tabs.
    storeAuthStateInCookie: false, // Set this to "true" if you are having issues on IE11 or Edge
  },
  system: {
    loggerOptions: {
      loggerCallback: (level: any, message: any, containsPii: any) => {
        if (containsPii) {
          return;
        }
        switch (level) {
          case LogLevel.Error:
            console.error(message);
            return;
          case LogLevel.Info:
            console.info(message);
            return;
          case LogLevel.Verbose:
            console.debug(message);
            return;
          case LogLevel.Warning:
            console.warn(message);
            return;
        }
      },
    },
  },
};

/**
 * Scopes you add here will be prompted for user consent during sign-in.
 * By default, MSAL.js will add OIDC scopes (openid, profile, email) to any login request.
 * For more information about OIDC scopes, visit:
 * https://docs.microsoft.com/en-us/azure/active-directory/develop/v2-permissions-and-consent#openid-connect-scopes
 */
export const loginRequest = {
  scopes: [
    `https://${tenantName}.onmicrosoft.com/${pamiApiName}/${pamiApiScopeRead}`,
    `https://${tenantName}.onmicrosoft.com/${pamiApiName}/${pamiApiScopeWrite}`,
  ],
};